import React from "react";
import { Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { ArrowRight, Quote } from "lucide-react";

/** Convert a YouTube / Vimeo / direct video URL into a renderable element. */
function VideoEmbed({ url }) {
  if (!url) return null;
  const u = url.trim();
  // YouTube
  const ytMatch = u.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  if (ytMatch) {
    return (
      <iframe
        title="About video"
        src={`https://www.youtube.com/embed/${ytMatch[1]}`}
        className="w-full h-full"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  }
  // Vimeo
  const vimeoMatch = u.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeoMatch) {
    return (
      <iframe
        title="About video"
        src={`https://player.vimeo.com/video/${vimeoMatch[1]}`}
        className="w-full h-full"
        frameBorder="0"
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
      />
    );
  }
  // Direct video file
  if (u.match(/\.(mp4|webm|ogg|mov)(\?|$)/i) || u.startsWith("data:video/")) {
    return <video src={u} controls className="w-full h-full object-cover" data-testid="about-video-direct"/>;
  }
  // Fallback — pop a link
  return (
    <a href={u} target="_blank" rel="noreferrer" className="w-full h-full flex items-center justify-center bg-[var(--muted)] font-mono text-sm underline">
      Watch video →
    </a>
  );
}

export default function About() {
  const { content } = useApp();
  const heroImg = content?.about_hero_image || "/condor-eagle-logo.png";
  const eyebrow = content?.about_eyebrow || "OUR STORY";
  const title = content?.about_title || "Owned & Operated by Native Americans";
  const intro = content?.about_intro || "";
  const sections = [
    { title: content?.about_section1_title, body: content?.about_section1_body, image: content?.about_section1_image },
    { title: content?.about_section2_title, body: content?.about_section2_body, image: content?.about_section2_image },
    { title: content?.about_section3_title, body: content?.about_section3_body, image: content?.about_section3_image },
  ].filter(s => s.title || s.body);
  const galleryImage = content?.about_gallery_image;
  const videoUrl = content?.about_video_url;
  const quote = content?.about_quote;
  const quoteAuthor = content?.about_quote_author;

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="border-b-2 border-[var(--ink)]">
        <div className="max-w-5xl mx-auto px-6 py-12 md:py-20 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="font-mono text-xs tracking-widest mb-4 text-[var(--accent)]" data-testid="about-eyebrow">{eyebrow}</div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl leading-[0.95] mb-6" data-testid="about-title">{title}</h1>
            {intro && <p className="text-base md:text-lg text-[var(--ink-2)] whitespace-pre-line" data-testid="about-intro">{intro}</p>}
          </div>
          <div className="relative">
            <div className="aspect-square border-2 border-[var(--ink)] bg-[var(--paper)] overflow-hidden">
              <img src={heroImg} alt={title} className="w-full h-full object-contain p-4" data-testid="about-hero-img"/>
            </div>
            <div className="absolute -bottom-3 -right-3 bg-[var(--accent-2)] border-2 border-[var(--ink)] px-4 py-2 font-display text-base md:text-lg rotate-3 text-[var(--ink)]">EST. 2015</div>
          </div>
        </div>
      </section>

      {/* Story sections */}
      <section className="max-w-4xl mx-auto px-6 py-16 space-y-16">
        {sections.map((s, i) => (
          <div key={s.title || i} className={`grid md:grid-cols-2 gap-8 items-center ${i % 2 === 1 ? 'md:[&>*:first-child]:order-2' : ''}`} data-testid={`about-section-${i+1}`}>
            <div>
              {s.title && <h2 className="font-display text-3xl md:text-4xl mb-4">{s.title}</h2>}
              {s.body && <p className="text-base md:text-lg text-[var(--ink-2)] whitespace-pre-line">{s.body}</p>}
            </div>
            {s.image && (
              <div className="aspect-[4/3] border-2 border-[var(--ink)] bg-[var(--muted)] overflow-hidden">
                <img src={s.image} alt={s.title || ""} className="w-full h-full object-cover"/>
              </div>
            )}
          </div>
        ))}
      </section>

      {/* Featured gallery image + video */}
      {(galleryImage || videoUrl) && (
        <section className="border-y-2 border-[var(--ink)] bg-[var(--muted)]/40">
          <div className="max-w-6xl mx-auto px-6 py-16">
            <div className={`grid gap-6 ${galleryImage && videoUrl ? 'md:grid-cols-2' : 'md:grid-cols-1'}`} data-testid="about-media-section">
              {galleryImage && (
                <div className="aspect-[16/10] border-2 border-[var(--ink)] bg-[var(--paper)] overflow-hidden" data-testid="about-gallery-image">
                  <img src={galleryImage} alt="Featured" className="w-full h-full object-cover"/>
                </div>
              )}
              {videoUrl && (
                <div className="aspect-[16/10] border-2 border-[var(--ink)] bg-[var(--ink)] overflow-hidden" data-testid="about-video">
                  <VideoEmbed url={videoUrl}/>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Quote callout */}
      {quote && (
        <section className="bg-[var(--teal)] text-[var(--paper)] border-y-2 border-[var(--ink)]">
          <div className="max-w-3xl mx-auto px-6 py-16 text-center">
            <Quote size={40} className="mx-auto mb-6 text-[var(--accent-2)]"/>
            <p className="font-display text-2xl md:text-4xl leading-tight mb-4" data-testid="about-quote">"{quote}"</p>
            {quoteAuthor && <p className="font-mono text-sm text-[var(--accent-2)]" data-testid="about-quote-author">{quoteAuthor}</p>}
          </div>
        </section>
      )}

      {/* CTA back to shop */}
      <section className="max-w-4xl mx-auto px-6 py-16 text-center">
        <h3 className="font-display text-3xl mb-4">SEE WHAT WE'RE OFFERING</h3>
        <Link to="/shop" className="btn-primary inline-flex items-center gap-2" data-testid="about-shop-cta">BROWSE INVENTORY <ArrowRight size={16}/></Link>
      </section>
    </div>
  );
}
